import React from 'react';
import { useHistory } from 'react-router-dom';
import { Footer, Footnote } from '@vwfs-bronson/bronson-react';
import { LegalFooterCMS, LegalFooterLinkCMS } from '@cp-shared-15/apis';
import { Override } from '@cp-shared-15/common-utilities';

export type LegalFooterProps = Override<
    LegalFooterCMS,
    {
        legalText?: string | React.ReactNode;
        metaSectionLabel?: string;
        trackingFunction?: (label: string, url: string) => void;
    }
>;

function isExternalLink(url: string): boolean {
    return /^(https?:\/\/|mailto:|tel:)/.test(url);
}

/**
 * A legal footer consolidating all necessary legal footprint
 */
export const LegalFooter: React.FC<LegalFooterProps> = ({
    links,
    legalText,
    copyrightText,
    footnotes,
    metaSectionLabel,
    footerAriaLabel,
    trackingFunction,
}) => {
    const history = useHistory();

    return (
        <Footer {...(footerAriaLabel && { 'aria-label': footerAriaLabel })}>
            <Footer.ModuleMetaNav copyrightText={copyrightText} metaSectionLabel={metaSectionLabel || ''}>
                {links.map(({ text, ariaLabel, title, url = '', externalUrl }: LegalFooterLinkCMS) => {
                    const linkTarget = externalUrl || url;
                    const isExternal = !!externalUrl || isExternalLink(linkTarget);

                    const handleClick = (e: React.MouseEvent) => {
                        if (trackingFunction) trackingFunction(text, linkTarget);

                        if (!isExternal && linkTarget.startsWith('/')) {
                            e.preventDefault();
                            history.push(linkTarget);
                        }
                    };

                    const linkProps: React.AnchorHTMLAttributes<HTMLAnchorElement> = {
                        ...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
                        title: title ?? text,
                        onClick: handleClick,
                    };

                    if (ariaLabel) {
                        linkProps['aria-label'] = ariaLabel;
                    }

                    return (
                        <Footer.ModuleMetaNav.Item
                            key={text}
                            href={linkTarget}
                            normalLink={isExternal}
                            linkProps={linkProps}
                        >
                            {text}
                        </Footer.ModuleMetaNav.Item>
                    );
                })}
            </Footer.ModuleMetaNav>

            {legalText || footnotes ? (
                <Footer.ModuleFootnotes footerText={legalText}>
                    {footnotes && (
                        <Footnote numbered>
                            {footnotes.map(footnote => (
                                <Footnote.Item key={footnote}>{footnote}</Footnote.Item>
                            ))}
                        </Footnote>
                    )}
                </Footer.ModuleFootnotes>
            ) : null}
        </Footer>
    );
};
